/**
 * Pointer and keyboard editing for the Overview grid (screenshots/74 #2, #3, #8; recipes O2–O4), driving the grid's
 * layout store (grid/layoutStore.ts: beginDrag → previewBox → drop / cancelDrag, pick → nudge):
 * - Mouse: drag a widget after 4px of movement. Touch / pen: long-press 300ms to pick up (page scroll still works).
 *   The lifted card follows the pointer through the CSS `translate` property (written straight to the DOM, never React
 *   state); the store's preview layout changes once per cell, so React re-renders once per cell, not per pointer move.
 * - Resize: 4 corner + 2 edge handles, snapping per cell; sizes a widget doesn't support are skipped (catalog sizes).
 * - Keyboard: Enter picks up / drops, arrows move (↑/↓ swap with the neighbour), Shift+arrows resize, Delete removes,
 *   Esc drops. Every committed change is announced ("Moved to column 5, row 3").
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { entryOf, sizeAllowed } from "./catalog";
import { COLS, MAX_H, MAX_W, MIN_H, MIN_W, ROW_STEP, GAP_PX as GAP, clampItem, type Dir as Arrow } from "./grid/layoutEngine";
import { overviewLayout, overviewStore } from "./overviewStore";
import type { LayoutItem } from "./types";

export type Box = Pick<LayoutItem, "x" | "y" | "w" | "h">;
export type Dir = "nw" | "ne" | "sw" | "se" | "e" | "s";
/** The widget under the pointer and the cells it started from (it's drawn there, offset by the pointer). */
export interface Lifted { id: string; origin: Box; mode: "drag" | "resize" }

const LONG_PRESS_MS = 300, SLOP = 4, TOUCH_SLOP = 8;
const same = (a: Box, b: Box) => a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
const ARROWS: Record<string, Arrow> = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down" };

export function useGridDrag(host: RefObject<HTMLDivElement | null>, colW: number) {
  const colStep = colW + GAP;
  const dragging = useRef(false);
  const [lifted, setLifted] = useState<Lifted | null>(null);

  // While a touch drag is live, stop the page from scrolling under the finger.
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const block = (e: TouchEvent) => { if (dragging.current) e.preventDefault(); };
    el.addEventListener("touchmove", block, { passive: false });
    return () => el.removeEventListener("touchmove", block);
  }, [host]);

  /** Drag a widget by its card. */
  const onCardPointerDown = useCallback((item: LayoutItem) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest("button, [data-handle], input, select, a, [role=menu]")) return;
    const el = e.currentTarget;
    const x0 = e.clientX, y0 = e.clientY;
    const touch = e.pointerType !== "mouse";
    let started = false, last: Box = item;
    let timer: ReturnType<typeof setTimeout> | undefined;
    overviewStore.getState().select(item.id);
    const start = () => {
      started = dragging.current = true;
      try { el.setPointerCapture(e.pointerId); } catch { /* the pointer already ended */ }
      el.classList.add("is-lifted");
      overviewLayout.getState().beginDrag(item.id);
      setLifted({ id: item.id, origin: { x: item.x, y: item.y, w: item.w, h: item.h }, mode: "drag" });
    };
    if (touch) timer = setTimeout(start, LONG_PRESS_MS);
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - x0, dy = ev.clientY - y0;
      if (!started) {
        if (touch) { if (Math.hypot(dx, dy) > TOUCH_SLOP) end(); return; }
        if (Math.hypot(dx, dy) < SLOP) return;
        start();
      }
      el.style.translate = `${dx}px ${dy}px`;
      const box = clampItem({ ...last, x: item.x + Math.round(dx / colStep), y: item.y + Math.round(dy / ROW_STEP) });
      if (!same(box, last)) { last = box; overviewLayout.getState().previewBox({ x: box.x, y: box.y }); }
    };
    const end = (ev?: PointerEvent) => {
      clearTimeout(timer);
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", end);
      removeEventListener("pointercancel", cancel);
      removeEventListener("keydown", esc);
      el.style.translate = "";
      el.classList.remove("is-lifted");
      dragging.current = false;
      if (started) { if (ev) overviewLayout.getState().drop("move"); else overviewLayout.getState().cancelDrag(); }
      setLifted(null);
    };
    const cancel = () => end();
    const esc = (k: KeyboardEvent) => { if (k.key === "Escape") { k.stopPropagation(); cancel(); } };
    addEventListener("pointermove", move);
    addEventListener("pointerup", end);
    addEventListener("pointercancel", cancel);
    addEventListener("keydown", esc);
  }, [colStep]);

  /** Resize from a corner or edge handle. */
  const onHandlePointerDown = useCallback((item: LayoutItem, dir: Dir) => (e: React.PointerEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const x0 = e.clientX, y0 = e.clientY;
    let last: Box = item;
    const entry = entryOf(item.type);
    dragging.current = true;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* the pointer already ended */ }
    overviewLayout.getState().beginDrag(item.id);
    setLifted({ id: item.id, origin: { x: item.x, y: item.y, w: item.w, h: item.h }, mode: "resize" });
    const move = (ev: PointerEvent) => {
      const dc = Math.round((ev.clientX - x0) / colStep), dr = Math.round((ev.clientY - y0) / ROW_STEP);
      let { x, y, w, h } = item;
      if (dir.includes("e")) w = item.w + dc;
      if (dir.includes("w")) { w = item.w - dc; x = item.x + dc; }
      if (dir.includes("s")) h = item.h + dr;
      if (dir.includes("n")) { h = item.h - dr; y = item.y + dr; }
      w = Math.min(MAX_W, Math.max(MIN_W, w)); h = Math.min(MAX_H, Math.max(MIN_H, h));
      if (dir.includes("w")) x = item.x + item.w - w;
      if (dir.includes("n")) y = Math.max(0, item.y + item.h - h);
      const box = { x: Math.max(0, Math.min(COLS - w, x)), y, w, h };
      if (!same(box, last) && sizeAllowed(entry, box.w, box.h)) { last = box; overviewLayout.getState().previewBox(box); }
    };
    const end = () => {
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", end);
      removeEventListener("pointercancel", end);
      dragging.current = false;
      overviewLayout.getState().drop("resize");
      setLifted(null);
    };
    addEventListener("pointermove", move);
    addEventListener("pointerup", end);
    addEventListener("pointercancel", end);
  }, [colStep]);

  /** Keyboard (74 #8): the store's pick-up / nudge, gated by the sizes each widget supports. */
  const onCardKeyDown = useCallback((item: LayoutItem) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    const st = overviewLayout.getState();
    const picked = st.picked === item.id;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      overviewStore.getState().select(item.id);
      st.pick(picked ? null : item.id);
    } else if (e.key === "Escape" && picked) {
      e.preventDefault(); e.stopPropagation();
      st.pick(null);
    } else if (ARROWS[e.key] && picked) {
      e.preventDefault();
      if (e.shiftKey) {
        const cur = st.layout.find((i) => i.id === item.id) ?? item;
        const d = ARROWS[e.key];
        const w = cur.w + (d === "right" ? 1 : d === "left" ? -1 : 0), h = cur.h + (d === "down" ? 1 : d === "up" ? -1 : 0);
        if (!sizeAllowed(entryOf(item.type), Math.max(MIN_W, w), Math.max(MIN_H, h))) return;
      }
      st.nudge(ARROWS[e.key], e.shiftKey);
    } else if ((e.key === "Delete" || e.key === "Backspace") && !picked) {
      e.preventDefault();
      overviewStore.getState().removeWidget(item.id);
    }
  }, []);

  return { lifted, onCardPointerDown, onHandlePointerDown, onCardKeyDown };
}
